import React, { type ComponentType, lazy, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { useSDMCartContext, useSDMCustomerContext, useSDMViewportContext } from '@ldp/sdm-contexts';
import { ThemeProvider, IViewResolverTheme } from '@ldp/view-resolver-utils';

import { getExistingCartGuid, getLoggedInStatus, getSiteId, isLoggedIn } from 'Utils/auth';
import Footer from '@/components/footer';
import Masthead from '@/components/masthead';
import { useCommonContextProps, useContentfulPreview } from '@/hooks';
import { getSelectedProvinceCookie } from '@/utils';
import { VIEW_RESOLVER, LoggedInStatus, environment } from '@/data/constants';
import { Spinner } from '@/components/shared';
import { IMastheadFooterContainer } from './masthead-footer.interface';

const MastheadFooterContainer: React.FC<IMastheadFooterContainer> = ({ children, isPhxPharmacyTab }) => {
  const { TENANT_ID_SDM, APP_TYPE_WEB, API_VERSION_V2, PAGE_ID_HEADER_FOOTER } = VIEW_RESOLVER;
  const { PRODUCTION, LOWER } = environment;
  const { renderDatePicker, isContentfulPreview, selectedDateTime } = useContentfulPreview();
  const { commonContextProps, locale } = useCommonContextProps();
  const router = useRouter();
  const preferredProvince = getSelectedProvinceCookie();
  const cartId = getLoggedInStatus() === LoggedInStatus.CURRENT ? '' : getExistingCartGuid();
  const headerSection = useMemo(() => ({}), []);
  const [ViewResolverSDM, setViewResolverSDM] = useState<ComponentType<any> | null>(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      // @ts-ignore
      setViewResolverSDM(lazy(() => import('ldpViewResolverSdm/Resolver')));
    }
  }, []);

  const headerData = {
    'x-loblaw-tenant-id': TENANT_ID_SDM,
    'x-application-type': APP_TYPE_WEB,
    'Accept-Language': locale,
    Authorization: isLoggedIn(),
    'Content-Type': 'application/json',
    'x-preview': isContentfulPreview,
    'x-preview-datetime': selectedDateTime,
  };

  const data = {
    banner: getSiteId(),
    cartId: cartId,
    ...(preferredProvince ? { province: preferredProvince } : {}),
  };

  const fetchOptions = {
    method: 'POST',
    body: JSON.stringify(data),
    headers: headerData,
  };

  const sdmContextProps = {
    sdmCartProviderProps: {
      ...useSDMCartContext(),
    },
    sdmCustomerProviderProps: {
      ...useSDMCustomerContext(),
    },
    sdmRouterProps: { ...router },
    sdmViewportProviderProps: {
      ...useSDMViewportContext(),
    },
  };

  const theme: IViewResolverTheme = {
    name: 'sdm-web',
    version: 'latest',
    env: process.env.NEXT_PUBLIC_VIEW_RESOLVER_ENV === PRODUCTION ? PRODUCTION : LOWER,
  };

  return (
    <div data-testid="main-page">
      {ViewResolverSDM ? (
        <ViewResolverSDM
          pageId={PAGE_ID_HEADER_FOOTER}
          fetchOptions={fetchOptions}
          commonContexts={commonContextProps}
          sdmContexts={sdmContextProps}
          locale={locale}
          PageLoadingFallback={() => <Spinner />}
          env={process.env.NEXT_PUBLIC_VIEW_RESOLVER_ENV}
          apiVersion={API_VERSION_V2}
        >
          <Masthead headerSection={headerSection} />
          <ThemeProvider key="non-sdui-theme" theme={theme}>
            {children}
          </ThemeProvider>
          {!isPhxPharmacyTab && <Footer data-testid="footer" />}
        </ViewResolverSDM>
      ) : (
        <Spinner />
      )}
      {isContentfulPreview ? renderDatePicker() : null}
    </div>
  );
};

export default MastheadFooterContainer;
